import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import TermsAndConditions from "./TermsAndConditions";
import DateUtils from "../utils/DateUtils";
import StringUtils from "../utils/StringUtils";
import TrustdService from "../service/TrustdService";
import { Button, Card, DateInput, Field, Modal, Spinner } from "../components/UI";
import { apiHeaders, useToast } from "../hooks";
import logo from "../assets/image.webp";
import { isHostawayGuestForm } from "../utils/GuestFormProvider";
import { formatGeneratedDate } from "../utils/GeneratedDate";

const blankGuestForm = {
  firstName: "",
  middleName: "",
  lastName: "",
  birthDate: "",
  checkInDate: "",
  checkOutDate: "",
  listingName: "",
  listingChannel: "",
  termsAndConditions: false,
};

const normalizeGuestLastName = (request) => {
  const lastName = request?.GuestInfo?.last_name || "";
  const fallbackApplied = Boolean(request?.Metadata?.guestbridgeFallbackLastNameApplied);
  return isHostawayGuestForm(request) && (fallbackApplied || lastName.trim().toLowerCase() === "unknown") ? "" : lastName;
};

export default function GuestForm({ id }) {
  const [guestForm, setGuestForm] = useState(blankGuestForm);
  const [exGuestForm, setExGuestForm] = useState(blankGuestForm);
  const [idVerificationRequired, setIdVerificationRequired] = useState(false);
  const [selfieVerificationRequired, setSelfieVerificationRequired] = useState(false);
  const [imageIsUploaded, setImageIsUploaded] = useState(false);
  const [file, setFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreviewUrl, setSelfiePreviewUrl] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [guestFullName, setGuestFullName] = useState("");
  const [hostEmail, setHostEmail] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [showFormSubmitDialog, setShowFormSubmitDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const selfieGalleryInputRef = useRef(null);
  const selfieCaptureInputRef = useRef(null);
  const selfieVideoRef = useRef(null);
  const selfieCanvasRef = useRef(null);
  const selfieStreamRef = useRef(null);
  const termsRef = useRef(null);
  const addToast = useToast();

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const response = await TrustdService.getGuestFormRequestById(id);
        const request = response.guestFormRequest;
        if (request !== null && request.Status === "pending") {
          const data = {
            firstName: request.GuestInfo.first_name || "",
            middleName: request.GuestInfo.middle_name || "",
            lastName: normalizeGuestLastName(request),
            birthDate: request.GuestInfo.dob || "",
            checkInDate: request.BookingInfo.check_in_date || "",
            checkOutDate: request.BookingInfo.check_out_date || "",
            listingName: request.BookingInfo.listing_name || "",
            listingChannel: request.BookingInfo.listing_channel || "",
            termsAndConditions: false,
          };
          setHostEmail(request.HostEmail);
          setGeneratedAt(request.CreatedAt);
          setGuestForm(data);
          setExGuestForm(data);
          setGuestEmail(request.GuestInfo.email || "");
          setGuestPhone(request.GuestInfo.phone || "");
          setBookingId(request.BookingInfo.booking_id || "");
          setIdVerificationRequired(Boolean(request.IdVerificationRequired ?? request.Metadata?.idVerificationRequired));
          setSelfieVerificationRequired(Boolean(request.SelfieVerificationRequired ?? request.Metadata?.selfieVerificationRequired));
          setGuestFullName([data.firstName, data.middleName, data.lastName].filter(Boolean).join(" "));
        } else {
          setFormSubmitted(true);
          setShowErrorDialog(true);
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  useEffect(() => () => {
    selfieStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  useEffect(() => () => {
    if (selfiePreviewUrl) URL.revokeObjectURL(selfiePreviewUrl);
  }, [selfiePreviewUrl]);

  const update = (field, value) => setGuestForm((form) => ({ ...form, [field]: value }));

  const handleFileChange = (event) => {
    const nextFile = event.target.files[0];
    if (!nextFile) return;
    setFile(nextFile);
    setImageIsUploaded(true);
    addToast({ severity: "success", summary: "Success", detail: "ID is uploaded successfully!", life: 5000 });
  };

  const stopCamera = () => {
    selfieStreamRef.current?.getTracks().forEach((track) => track.stop());
    selfieStreamRef.current = null;
    if (selfieVideoRef.current) selfieVideoRef.current.srcObject = null;
    setCameraOpen(false);
    setCameraStarting(false);
  };

  const selectSelfie = (nextFile) => {
    if (!nextFile) return;
    if (!["image/jpeg", "image/png"].includes(nextFile.type)) {
      addToast({ severity: "error", summary: "Unsupported Image", detail: "Please choose a JPG or PNG image.", life: 7000 });
      return;
    }
    if (nextFile.size > 5 * 1024 * 1024) {
      addToast({ severity: "error", summary: "Image Too Large", detail: "Your selfie must be 5 MB or smaller.", life: 7000 });
      return;
    }
    setSelfieFile(nextFile);
    setSelfiePreviewUrl(URL.createObjectURL(nextFile));
    setCameraError("");
    stopCamera();
    addToast({ severity: "success", summary: "Selfie Ready", detail: "Your selfie is ready for secure verification.", life: 5000 });
  };

  const handleSelfieFileChange = (event) => {
    selectSelfie(event.target.files?.[0]);
    event.target.value = "";
  };

  const openCamera = async () => {
    setCameraError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      selfieCaptureInputRef.current?.click();
      return;
    }
    setCameraOpen(true);
    setCameraStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
      });
      selfieStreamRef.current = stream;
      if (selfieVideoRef.current) {
        selfieVideoRef.current.srcObject = stream;
        await selfieVideoRef.current.play();
      }
    } catch {
      stopCamera();
      setCameraError("Camera access was unavailable. You can still choose a photo from your device.");
    } finally {
      setCameraStarting(false);
    }
  };

  const captureSelfie = () => {
    const video = selfieVideoRef.current;
    const canvas = selfieCanvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return;
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");
    context.translate(size, 0);
    context.scale(-1, 1);
    context.drawImage(video, (video.videoWidth - size) / 2, (video.videoHeight - size) / 2, size, size, 0, 0, size, size);
    canvas.toBlob((blob) => {
      if (blob) selectSelfie(new File([blob], `selfie-${Date.now()}.jpg`, { type: "image/jpeg" }));
    }, "image/jpeg", 0.9);
  };

  const uploadFile = async (reference) => {
    if (!file) return false;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("first_name", guestForm.firstName);
    formData.append("last_name", guestForm.lastName);
    formData.append("birth_date", guestForm.birthDate);
    formData.append("guest_form_id", id);
    if (reference) {
      formData.append("trustd_reference", reference);
    }
    const response = await axios.post("/v1/integrations/stripe/requests/id-verification", formData, {
      headers: { "x-api-key": apiHeaders["x-api-key"] },
    });
    return response.data;
  };

  const uploadSelfie = async (reference) => {
    const formData = new FormData();
    formData.append("file", selfieFile);
    formData.append("guest_form_id", id);
    if (reference) formData.append("trustd_reference", reference);
    const response = await axios.post("/v1/integrations/stripe/requests/selfie-verification", formData, {
      headers: { "x-api-key": apiHeaders["x-api-key"] },
    });
    return response.data;
  };

  const validateFields = () => {
    let valid = true;
    if (StringUtils.isEmpty(guestForm.firstName)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "First Name is required", life: 5000 });
      valid = false;
    }
    if (StringUtils.isEmpty(guestForm.lastName)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Last Name is required", life: 5000 });
      valid = false;
    }
    if (!DateUtils.isValid(guestForm.birthDate)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Date of Birth is required", life: 5000 });
      valid = false;
    }
    if (!DateUtils.isValid(guestForm.checkInDate)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Check-in Date is required", life: 5000 });
      valid = false;
    }
    if (!DateUtils.isValid(guestForm.checkOutDate)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Check-out Date is required", life: 5000 });
      valid = false;
    }
    if (!guestForm.termsAndConditions) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Please check terms and conditions", life: 5000 });
      valid = false;
    }
    return valid;
  };

  const submit = async () => {
    if (submitInProgress) {
      addToast({ severity: "error", summary: "Guest Form Save Error", detail: "Submit in progress! Please wait...", life: 5000 });
      return;
    }
    if (idVerificationRequired && !imageIsUploaded) {
      addToast({ severity: "error", summary: "Guest Form Save Error", detail: "You must upload your ID for verification!", life: 5000 });
      return;
    }
    if (selfieVerificationRequired && !selfieFile) {
      addToast({ severity: "error", summary: "Selfie Required", detail: "Please take a selfie or choose one from your gallery.", life: 7000 });
      return;
    }
    if (!validateFields()) return;

    setSubmitInProgress(true);
    setLoading(true);
    try {
      if (idVerificationRequired) {
        try {
          await uploadFile();
        } catch {
          addToast({ severity: "error", summary: "ID Upload Error", detail: "Your ID could not be stored securely. Please try again.", life: 10000 });
          return;
        }
      }
      if (selfieVerificationRequired) {
        try {
          await uploadSelfie();
        } catch {
          addToast({ severity: "error", summary: "Selfie Upload Error", detail: "Your selfie could not be verified securely. Please try again.", life: 10000 });
          return;
        }
      }

      const guest = {
        email: guestEmail,
        phone: guestPhone,
        firstName: guestForm.firstName,
        middleName: guestForm.middleName,
        lastName: guestForm.lastName,
        birthDate: guestForm.birthDate,
      };
      const booking = {
        bookingId,
        listingName: guestForm.listingName,
        listingChannel: guestForm.listingChannel,
        checkInDate: guestForm.checkInDate,
        checkOutDate: guestForm.checkOutDate,
      };
      const response = await TrustdService.updateGuestFormRequest(id, guest, booking, "completed", null, hostEmail);

      if (response) {
        setShowFormSubmitDialog(true);
      } else {
        addToast({ severity: "error", summary: "Guest Form Save Error", detail: "Guest Form could not be saved!", life: 5000 });
      }
    } finally {
      setSubmitInProgress(false);
      setLoading(false);
    }
  };

  if (loading) return <Spinner />;

  if (formSubmitted) {
    return (
      <div className="submitInformation">
        <Card className="cardCenter cardW50 submitInformationCard">
          <div className="submitInformationText">
            {showErrorDialog ? "This link has already used or is not working anymore. If you think there is an issue please contact us at info@trustd.ai with URL address above." : "You can close this page."}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="cardContainer secureGuestForm containerOverFlow mt-0 guest-form-modern">
        <Card
          className="cardCenter cardW75 guest-form-card"
          title={
            <div className="guest-form-head">
              <div className="guest-form-brand">
                <img alt="Trustd logo" src={logo} height="40" className="guest-form-logo" />
                <span>Trustd Secure Guest Form</span>
                <span className="guest-form-security-pill">Encrypted Submission</span>
              </div>
              <h1>Complete your guest verification</h1>
              <p className="guest-form-generated-at">
                <i className="pi pi-calendar" aria-hidden="true" />
                Generated: {formatGeneratedDate(generatedAt)}
              </p>
              <p className="guest-form-intro">
                Hello {guestFullName || "Guest"}. Thank you for your recent booking {guestForm.listingName ? `at ${guestForm.listingName}` : "at our property"}.
                Please fill the fields below so your host can complete the verification process securely.
              </p>
              <div className="guest-form-stay-meta">
                <div><span>Check-in</span><strong>{guestForm.checkInDate || "-"}</strong></div>
                <div><span>Check-out</span><strong>{guestForm.checkOutDate || "-"}</strong></div>
                <div><span>Listing</span><strong>{guestForm.listingName || "Property"}</strong></div>
              </div>
            </div>
          }
          footer={
            <div className="guest-form-footer">
              <label className="guest-form-consent">
                <input type="checkbox" className="form-check-input" checked={guestForm.termsAndConditions} onChange={(event) => update("termsAndConditions", event.target.checked)} />
                <span className="form-check-label">
                  By clicking here, I state that I read, understood, and accepted the terms of{" "}
                  <strong onClick={() => termsRef.current?.openGuestConsentForm()}>Guest Consent Form</strong>,{" "}
                  <strong onClick={() => termsRef.current?.openTermsOfUse()}>Terms of Use</strong>, and{" "}
                  <strong onClick={() => termsRef.current?.openPrivacyPolicy()}>Privacy Policy</strong>.
                </span>
              </label>
              <div className="guest-form-submit-row">
                <Button onClick={submit}>
                  <i className="pi pi-lock" aria-hidden="true" />
                  <span>{submitInProgress ? "Submitting..." : "Submit Securely"}</span>
                </Button>
              </div>
            </div>
          }
        >
          <div className="guest-form-fields">
            <div className="guest-form-section-heading">
              <h2>Guest Information</h2>
              <p>Please review and complete all required fields before submission.</p>
            </div>
            {idVerificationRequired ? (
              <Field label="Photo ID" required className="mt-3 guest-form-upload-field">
                <div className="guest-id-card">
                  {!imageIsUploaded ? (
                    <div onClick={() => fileInputRef.current?.click()} className="custom-upload-area guest-form-upload-area" role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") fileInputRef.current?.click(); }}>
                      <div className="guest-form-upload-icon"><i className="pi pi-id-card" aria-hidden="true" /></div>
                      <p>Your host requires a picture ID for verification. Accepted file formats: .jpg, .jpeg, .png.</p>
                      <div className="btn btn-primary btn-lg">
                        <i className="pi pi-upload" aria-hidden="true" />
                        <span>Upload Image</span>
                      </div>
                    </div>
                  ) : (
                    <div className="custom-upload-area guest-form-upload-area">
                      <div className="guest-form-upload-icon"><i className="pi pi-check-circle" aria-hidden="true" /></div>
                      <p>ID image selected. We will store it securely and show any verification details to your host.</p>
                      <button type="button" className="btn btn-secondary btn-lg" onClick={() => fileInputRef.current?.click()}>
                        <i className="pi pi-refresh" aria-hidden="true" />
                        <span>Replace Image</span>
                      </button>
                    </div>
                  )}
                  <div className="guest-verification-privacy">
                    <i className="pi pi-lock" aria-hidden="true" />
                    <span><strong>Your privacy matters:</strong> Your ID will be securely deleted after you check out.</span>
                  </div>
                </div>
                <input ref={fileInputRef} type="file" className="form-control hidden" accept=".jpg,.jpeg,.png" onChange={handleFileChange} />
              </Field>
            ) : null}
            {selfieVerificationRequired ? (
              <Field label="Selfie" required className="mt-3 guest-form-upload-field">
                <div className="guest-selfie-card">
                  {!selfieFile && !cameraOpen ? (
                    <div className="guest-selfie-intro">
                      <div className="guest-form-upload-icon"><i className="pi pi-camera" aria-hidden="true" /></div>
                      <div>
                        <strong>Take a selfie</strong>
                        <p>Your host requires a selfie for verification. Your selfie will be compared with the photo on your ID to verify your identity. Accepted file formats: .jpg, .jpeg, .png.</p>
                      </div>
                      <div className="guest-selfie-actions">
                        <button type="button" className="btn btn-primary" onClick={openCamera}><i className="pi pi-camera" aria-hidden="true" /><span>Use Camera</span></button>
                        <button type="button" className="btn btn-secondary" onClick={() => selfieGalleryInputRef.current?.click()}><i className="pi pi-images" aria-hidden="true" /><span>Choose from Gallery</span></button>
                      </div>
                    </div>
                  ) : null}
                  {cameraOpen ? (
                    <div className="guest-selfie-camera">
                      <div className="guest-selfie-video-frame">
                        <video ref={selfieVideoRef} autoPlay muted playsInline aria-label="Live selfie camera preview" />
                        <div className="guest-selfie-face-guide" aria-hidden="true" />
                        {cameraStarting ? <div className="guest-selfie-camera-loading"><i className="pi pi-spin pi-spinner" /> Starting camera...</div> : null}
                      </div>
                      <p>Center your face in the guide and use even lighting.</p>
                      <div className="guest-selfie-actions">
                        <button type="button" className="btn btn-primary" onClick={captureSelfie} disabled={cameraStarting}><i className="pi pi-camera" /><span>Take Photo</span></button>
                        <button type="button" className="btn btn-secondary" onClick={stopCamera}>Cancel</button>
                      </div>
                    </div>
                  ) : null}
                  {selfieFile ? (
                    <div className="guest-selfie-preview">
                      <img src={selfiePreviewUrl} alt="Selected selfie preview" />
                      <div>
                        <span className="guest-selfie-ready"><i className="pi pi-check-circle" /> Selfie ready</span>
                        <strong>Make sure your face is clear and unobstructed.</strong>
                        <p>{selfieFile.name}</p>
                        <div className="guest-selfie-actions">
                          <button type="button" className="btn btn-primary" onClick={openCamera}><i className="pi pi-refresh" /><span>Retake</span></button>
                          <button type="button" className="btn btn-secondary" onClick={() => selfieGalleryInputRef.current?.click()}><i className="pi pi-images" /><span>Choose Another</span></button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {cameraError ? <div className="guest-selfie-error" role="alert"><i className="pi pi-exclamation-circle" />{cameraError}</div> : null}
                  <div className="guest-verification-privacy">
                    <i className="pi pi-lock" aria-hidden="true" />
                    <span><strong>Your privacy matters:</strong> Your selfie will be securely deleted as soon as the verification process is completed.</span>
                  </div>
                </div>
                <input ref={selfieGalleryInputRef} type="file" className="form-control hidden" accept="image/jpeg,image/png,.jpg,.jpeg,.png" onChange={handleSelfieFileChange} />
                <input ref={selfieCaptureInputRef} type="file" className="form-control hidden" accept="image/jpeg,image/png" capture="user" onChange={handleSelfieFileChange} />
                <canvas ref={selfieCanvasRef} className="hidden" />
              </Field>
            ) : null}

            <div className="guest-form-grid">
              <Field label="First Name" required className="mt-3"><input className="form-control" disabled={Boolean(exGuestForm.firstName)} value={guestForm.firstName} onChange={(event) => update("firstName", event.target.value)} /></Field>
              <Field label="Middle Name" className="mt-3"><input className="form-control" disabled={Boolean(exGuestForm.middleName)} value={guestForm.middleName} onChange={(event) => update("middleName", event.target.value)} /></Field>
              <Field label="Last Name" required className="mt-3"><input className="form-control" disabled={Boolean(exGuestForm.lastName)} value={guestForm.lastName} onChange={(event) => update("lastName", event.target.value)} /></Field>
              <Field label="Date of Birth" required className="mt-3"><DateInput value={guestForm.birthDate} onChange={(value) => update("birthDate", value)} /></Field>
              <Field label="Check-in Date" className="mt-3"><DateInput value={guestForm.checkInDate} disabled={Boolean(exGuestForm.checkInDate)} onChange={(value) => update("checkInDate", value)} /></Field>
              <Field label="Check-out Date" className="mt-3"><DateInput value={guestForm.checkOutDate} disabled={Boolean(exGuestForm.checkOutDate)} onChange={(value) => update("checkOutDate", value)} /></Field>
              <Field label="Listing" className="mt-3"><input className="form-control" value={guestForm.listingName} disabled readOnly /></Field>
              <Field label="Listing Channel" className="mt-3"><input className="form-control" value={guestForm.listingChannel} disabled readOnly /></Field>
            </div>
          </div>
        </Card>
      </div>

      <Modal visible={showFormSubmitDialog} title="Guest Form" icon="pi-check-circle" tone="success" footer={<Button label="Close" onClick={() => { setShowFormSubmitDialog(false); setFormSubmitted(true); }} />}>
        <span>Guest Form is submitted securely. Thank you.</span>
      </Modal>
      <TermsAndConditions ref={termsRef} onAccept={() => update("termsAndConditions", true)} />
    </>
  );
}
